import { ComponentProps, Dispatch, MouseEvent, SetStateAction, useState } from 'react'
import { createPortal } from 'react-dom'
import { ActivityIndicator, Button, Dialog, TextArea } from '@bosch/react-frok'
import { useTranslation } from 'react-i18next'
import InputAudio from '@/ui/components/InputAudio/InputAudio'
import MicrophoneButton from '@/ui/components/InputAudio/MicrophoneButton'
import useAudioRecording from '@/hooks/useAudioRecording'
import { useSpace } from '@/hooks/useSpace'
import { useConfig } from '@/hooks/useConfig'
import { useAuthentication } from '@/hooks/useAuthentication'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'
import { AssistantAction, getAssistantIllustration } from '@/hooks/useAssistantIllustration'
import { sendTeamsNotification, TeamsEndpointUnavailableError } from '@/utils/api/teamsNotification'
import { buildIssueReportLines, formatIssueReport, resolveIssueReportFields } from '@/utils/issueReport'
import type { IssueReportFieldKey, IssueReportLine } from '@/utils/issueReport'
import MsTeamLogo from '@/assets/teams-logo.png'
import MsTeamIcon from '@/assets/ms-teams.svg'
import MsTeamReportSentImage from '@/assets/bug-report-sent.svg'

import './IssueReportDialog.scss'

/**
 * How the report gets to the developers:
 * - `with-flow`: the user describes the problem, the backend posts it to the Teams channel (Power Automate flow).
 * - `without-flow`: no backend call; the dialog shows the information of the error to copy, and a button that copies
 *   it and opens the Teams channel "User Feedbacks", where the user posts it.
 */
export type IssueReportDialogType = 'with-flow' | 'without-flow'

type BugReportStatus = 'idle' | 'loading' | 'succeeded' | 'failed' | 'fallback'

/** How long the copy button shows that the report is copied, before it shows the copy icon again */
export const COPIED_STATE_DURATION_MS = 3000

/** How long the Teams app gets to answer before the channel opens in the browser */
const TEAMS_APP_TIMEOUT_MS = 1500

interface IssueReportDialogProps {
    open: boolean
    errorPayload?: Record<string, unknown>
    onClose: () => void
    type?: IssueReportDialogType
    /**
     * The fields of the information of the error that the dialog shows (without flow), in this order. Without it the
     * list of the config (`contact.issueReportFields`) is taken, and without that all fields (see `utils/issueReport`).
     */
    reportFields?: IssueReportFieldKey[]
}

/** Opens the channel in the Teams app, and in the browser when the app does not answer within 1.5 seconds. */
const openTeamsChannel = (webUrl: string) => {
    if (!webUrl) return

    const nativeUrl = webUrl.replace('https://teams.microsoft.com', 'msteams:')
    const fallbackTimer = setTimeout(() => {
        window.open(webUrl, '_blank', 'noreferrer')
    }, TEAMS_APP_TIMEOUT_MS)

    window.addEventListener('blur', () => clearTimeout(fallbackTimer), { once: true })
    window.location.href = nativeUrl
}

/** The message to show for a failed send: the `detail` of the backend when the error carries it, else the raw message. */
const getBugReportErrorMessage = (error: unknown): string | null => {
    const rawMessage = (error as { message?: string } | null)?.message ?? ''
    const jsonText = /\{[\s\S]*\}/.exec(rawMessage)?.[0]

    if (jsonText) {
        try {
            const detail = JSON.parse(jsonText)?.detail
            if (detail) return detail
        } catch {
            // not JSON: the raw message is shown
        }
    }
    return rawMessage || null
}

const SmallActivityIndicator = () => <ActivityIndicator size="small" />

const ReportMetadataLabel = () => {
    const { t } = useTranslation()

    return (
        <span className="bug-report-dialog__metadata-label">
            <i className="a-icon boschicon-bosch-ic-paperclip" aria-hidden="true" />
            {t('chat:bugReportDialog:metadataLabel')}
        </span>
    )
}

interface CloseButtonProps {
    onClose: () => void
}

const CloseButton = ({ onClose }: CloseButtonProps) => {
    const { t } = useTranslation()

    return (
        <Button
            icon={{ iconName: 'close', title: t('chat:bugReportDialog:closeButton') }}
            label={t('chat:bugReportDialog:closeButton')}
            mode="primary"
            onClick={onClose}
        />
    )
}

interface CopyableReportProps {
    reportLines: IssueReportLine[]
    channelUrl: string
}

/** Without flow: the information of the error to copy, and the button that copies it and opens the channel */
const CopyableReport = ({ reportLines, channelUrl }: CopyableReportProps) => {
    const { t } = useTranslation()
    const { copyIcon, contentCopied, handleCopy } = useCopyToClipboard(COPIED_STATE_DURATION_MS)
    const reportText = formatIssueReport(reportLines)

    // When the browser does not let us copy, the channel opens all the same: the text of the box can be selected
    const handleCopyAndOpenChannel = async () => {
        await handleCopy(reportText)
        openTeamsChannel(channelUrl)
    }

    return (
        <>
            <div className="bug-report-dialog__note">{t('chat:bugReportDialog:noFlowNote')}</div>
            <div className="bug-report-dialog__report" data-testid="issue-report-content">
                <div className="bug-report-dialog__report-content">
                    <div className="bug-report-dialog__report-header">
                        <ReportMetadataLabel />
                        <Button
                            className="bug-report-dialog__copy-button"
                            mode="integrated"
                            icon={{
                                iconName: copyIcon,
                                title: t(
                                    contentCopied ? 'chat:bugReportDialog:copiedMessage' : 'chat:bugReportDialog:copyButton'
                                ),
                            }}
                            onClick={() => handleCopy(reportText)}
                        />
                    </div>
                    <dl className="bug-report-dialog__report-lines">
                        {reportLines.map((line) => (
                            <div key={line.label} className="bug-report-dialog__report-line">
                                <dt>{line.label}</dt>
                                <dd>{line.value}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>

            <div className="bug-report-dialog__actions">
                {channelUrl ? (
                    <Button
                        icon={{
                            iconName: 'externallink',
                            title: t('chat:bugReportDialog:openChannelButton'),
                        }}
                        label={t('chat:bugReportDialog:openChannelButton')}
                        mode="primary"
                        onClick={handleCopyAndOpenChannel}
                    />
                ) : (
                    <p className="bug-report-dialog__fallback-text">{t('chat:bugReportDialog:channelUnavailable')}</p>
                )}
            </div>
        </>
    )
}

/** With flow: the backend has no endpoint for the report */
const EndpointUnavailable = ({ onClose }: CloseButtonProps) => {
    const { t } = useTranslation()

    return (
        <div className="bug-report-dialog__fallback">
            <p className="bug-report-dialog__fallback-text">{t('chat:bugReportDialog:endpointUnavailable')}</p>
            <CloseButton onClose={onClose} />
        </div>
    )
}

interface ReportSentProps extends CloseButtonProps {
    onSwitchToTeams: (event: MouseEvent<HTMLAnchorElement>) => void
    channelUrl: string
}

/** With flow: the report is posted */
const ReportSent = ({ onClose, onSwitchToTeams, channelUrl }: ReportSentProps) => {
    const { t } = useTranslation()

    return (
        <div className="bug-report-dialog__success">
            <img src={MsTeamReportSentImage} className="bug-report-dialog__success-image" alt="" aria-hidden="true" />
            <p className="bug-report-dialog__success-text">{t('chat:bugReportDialog:sentMessage')}</p>
            <a
                href={channelUrl || '#'}
                target="_blank"
                rel="noreferrer"
                className="bug-report-dialog__teams-link"
                onClick={onSwitchToTeams}
            >
                <img src={MsTeamIcon} className="ms-teams-icon" alt="" aria-hidden="true" />
                {t('chat:bugReportDialog:switchToTeams')}
            </a>
            <CloseButton onClose={onClose} />
        </div>
    )
}

interface ReportFormProps {
    assistant: ComponentProps<typeof InputAudio>['assistant']
    status: BugReportStatus
    errorMessage: string | null
    description: string
    onDescriptionChange: Dispatch<SetStateAction<string>>
    errorId: string
    chatId: string
    onSend: () => void
}

/** With flow: the user describes the problem and sends it */
const ReportForm = ({
    assistant,
    status,
    errorMessage,
    description,
    onDescriptionChange,
    errorId,
    chatId,
    onSend,
}: ReportFormProps) => {
    const { t } = useTranslation()
    const { audioInputProps, isRecording, isTranscribing, toggleRecording } = useAudioRecording()
    const isSending = status === 'loading'

    return (
        <>
            {status === 'failed' && (
                <div className="bug-report-dialog__error">
                    <i className="a-icon boschicon-bosch-ic-alert-error" aria-hidden="true" />
                    <span>{errorMessage ?? t('chat:bugReportDialog:sendError')}</span>
                </div>
            )}
            <div className="bug-report-dialog__note">{t('chat:bugReportDialog:note')}</div>

            <div className="bug-report-dialog__input-wrapper">
                {isTranscribing && (
                    <div className="bug-report-dialog__transcribing-overlay">
                        <SmallActivityIndicator />
                    </div>
                )}
                <TextArea
                    id="bug-report-description"
                    className="bug-report-dialog__textarea"
                    label={t('chat:bugReportDialog:helperText')}
                    value={description}
                    dynamicHeight
                    onChange={(event) => onDescriptionChange(event.target.value)}
                    disabled={isSending || isTranscribing}
                />
                <div className="bug-report-dialog__input-actions">
                    <div className="bug-report-dialog__input-actions-left">
                        <InputAudio
                            {...audioInputProps}
                            assistant={assistant}
                            setQuestion={onDescriptionChange}
                            validateTokenAndRehydrate={undefined}
                        />
                    </div>
                    <div className="bug-report-dialog__input-actions-right">
                        <MicrophoneButton
                            className="bug-report-dialog__mic-button"
                            isRecording={isRecording}
                            onClick={toggleRecording}
                            disabled={isSending}
                        />
                    </div>
                </div>
            </div>

            {errorId && (
                <div className="bug-report-dialog__metadata">
                    <div className="bug-report-dialog__metadata-img">
                        <img src={getAssistantIllustration(AssistantAction.CRYING)} alt="" aria-hidden="true" />
                    </div>
                    <div className="bug-report-dialog__metadata-content">
                        <ReportMetadataLabel />
                        <div className="bug-report-dialog__metadata-row">
                            <span className="bug-report-dialog__error-id">{`Error ID: ${errorId}`}</span>
                        </div>
                        <div className="bug-report-dialog__metadata-row">
                            <span className="bug-report-dialog__session_link">{`Session ID: ${chatId}`}</span>
                        </div>
                    </div>
                </div>
            )}

            <div className="bug-report-dialog__actions">
                {isSending ? (
                    <SmallActivityIndicator />
                ) : (
                    <Button
                        icon={{
                            iconName: 'paperplane',
                            title: t('chat:bugReportDialog:sendButton'),
                        }}
                        label={t('chat:bugReportDialog:sendButton')}
                        mode="primary"
                        onClick={onSend}
                        disabled={!description.trim()}
                    />
                )}
            </div>
        </>
    )
}

const IssueReportDialog = ({
    open,
    errorPayload,
    onClose,
    type = 'with-flow',
    reportFields,
}: IssueReportDialogProps) => {
    const { t } = useTranslation()
    const { getActiveAssistant, getActiveSpace } = useSpace()
    const { getConfig } = useConfig()
    const { information, getApiToken } = useAuthentication()
    const appConfig = getConfig()
    const assistant = getActiveAssistant()
    const activeSpace = getActiveSpace?.()
    const chatId = assistant?.activeChat?.chatId ?? ''
    const [bugReportStatus, setBugReportStatus] = useState<BugReportStatus>('idle')
    const [bugReportError, setBugReportError] = useState<string | null>(null)
    const [description, setDescription] = useState('')
    const [postedChannelUrl, setPostedChannelUrl] = useState('')

    const errorId = String(errorPayload?.error_id ?? '')
    const configuredChannelUrl = appConfig?.contact?.teamsChannelUrl ?? ''
    const channelUrl = postedChannelUrl || configuredChannelUrl

    // The information of the error that the user posts in the channel (without flow)
    const reportLines = buildIssueReportLines(
        errorPayload,
        {
            sessionId: chatId,
            spaceId: activeSpace?.id,
            assistantId: assistant?.config?.endpoint,
        },
        resolveIssueReportFields(reportFields ?? appConfig?.contact?.issueReportFields)
    )

    const handleSend = async () => {
        const backendEndpoint = appConfig?.endpoint
        if (!backendEndpoint) return

        setBugReportStatus('loading')
        setBugReportError(null)
        try {
            const token = await getApiToken()
            const channelUrlOfPost = await sendTeamsNotification(
                backendEndpoint,
                activeSpace?.id ?? '',
                {
                    ...errorPayload,
                    userDescription: description,
                    reporterName: information?.account?.name ?? 'Unknown',
                    reporterEmail: information?.account?.username ?? '',
                    errorId,
                    reporterOid: information?.userId ?? '',
                    sessionId: chatId,
                },
                token,
                information?.userId
            )
            setPostedChannelUrl(channelUrlOfPost || configuredChannelUrl)
            setBugReportStatus('succeeded')
            setDescription('')
        } catch (error) {
            if (error instanceof TeamsEndpointUnavailableError) {
                setBugReportStatus('fallback')
                return
            }
            setBugReportError(getBugReportErrorMessage(error))
            setBugReportStatus('failed')
        }
    }

    const handleClose = () => {
        setDescription('')
        setBugReportStatus('idle')
        setBugReportError(null)
        setPostedChannelUrl('')
        onClose()
    }

    const handleSwitchToTeams = (event: MouseEvent<HTMLAnchorElement>) => {
        handleClose()
        event.preventDefault()
        openTeamsChannel(channelUrl)
    }

    const renderContent = () => {
        if (type === 'without-flow') return <CopyableReport reportLines={reportLines} channelUrl={channelUrl} />
        if (bugReportStatus === 'fallback') return <EndpointUnavailable onClose={handleClose} />
        if (bugReportStatus === 'succeeded') {
            return <ReportSent onClose={handleClose} onSwitchToTeams={handleSwitchToTeams} channelUrl={channelUrl} />
        }

        return (
            <ReportForm
                assistant={assistant}
                status={bugReportStatus}
                errorMessage={bugReportError}
                description={description}
                onDescriptionChange={setDescription}
                errorId={errorId}
                chatId={chatId}
                onSend={handleSend}
            />
        )
    }

    const dialogTitle = (
        <span className="bug-report-dialog-title">
            <img src={MsTeamLogo} className="bug-report-dialog-title__logo" alt="" aria-hidden="true" />
            <span>{t('chat:bugReportDialog:title')}</span>
        </span>
    ) as unknown as string

    // In the body, not in the message: a parent with a transform or an animation would make the backdrop (position:
    // fixed) as big as the message instead of the whole page
    return createPortal(
        <Dialog open={open} modal title={dialogTitle} onClose={handleClose} confirmLabel="" cancelLabel="">
            <div className="bug-report-dialog hide-modal-header-section">{renderContent()}</div>
        </Dialog>,
        document.body
    )
}

export default IssueReportDialog
